import * as React from 'react';
import { Banner, Input, Select, Skeleton } from '@teamsnap/snap-ui';
import { Controller, FormContextValues } from 'react-hook-form';

// Local Imports
import { SelectOption } from '@teamsnap/snap-ui/build/components/Select/Select.types';
import useApiGetNgbIntegrations from '../hooks/useApiGetNgbIntegrations';
import NgbIntegrationFields from './NgbIntegrationFields';
import { DuplicateReg2FormModalInputs } from './DuplicateReg2FormModal';
import { FeatureFlagService } from 'frontend-toolkit';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { useParams } from 'react-router';
import ENVIRONMENT from 'core/environment';

type Props = {
  reactHookForm: FormContextValues<DuplicateReg2FormModalInputs>;
  formData: Partial<DuplicateReg2FormModalInputs>;
  programOptions: SelectOption[];
  seasonOptions: SelectOption[];
  seasonDifferentFromOriginal: boolean;
};

const DuplicateReg2FormStepOne: React.FC<Props> = ({
  reactHookForm,
  formData,
  programOptions,
  seasonOptions,
  seasonDifferentFromOriginal,
}) => {
  const params = useParams<{ orgId: string }>();
  const isNgbIntegrationFeatureEnabled =
    FeatureFlagService.isFeatureEnabledForOrg(
      FeatureFlagConfig[FeatureFlags.NGB_INTEGRATIONS],
      ENVIRONMENT,
      Number(params.orgId)
    );
  const isHCRFeatureEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.HCR_REG_2],
    ENVIRONMENT,
    Number(params.orgId)
  );
  const seasonId = reactHookForm.watch('season');
  const [ngbIntegrationsRawData, isLoading] = useApiGetNgbIntegrations(
    isNgbIntegrationFeatureEnabled ? Number(seasonId) : null
  );
  const ngbIntegrationData = React.useMemo(
    () => (ngbIntegrationsRawData?.ngbIntegrations || [undefined])[0],
    [ngbIntegrationsRawData]
  );

  const shouldHideHCR = 
    !isHCRFeatureEnabled && (ngbIntegrationData?.code === 'hockey_canada');
  return (
    <div data-testid="DuplicateReg2FormStepOne--container">
      <Banner
        sentiment="info"
        caption={
          seasonDifferentFromOriginal ? (
            <ul className="sui-pl-2">
              <li>
                New forms must be manually opened or have the registration
                period set after they are created.
              </li>
              <li>
                This form is being moved from its original season, Auto-Roster
                rules will not be copied.
              </li>
            </ul>
          ) : (
            'New forms must be manually opened or have the registration period set after they are created.'
          )
        }
        className="sui-mb-2"
      />
      <div>
        <Controller
          as={
            <Input
              label="Registration name"
              required
              type="text"
              name="name"
              errors={
                reactHookForm.errors.name
                  ? [reactHookForm.errors.name.message as string]
                  : []
              }
            />
          }
          name="name"
          control={reactHookForm.control}
          defaultValue={''}
          rules={{
            required: 'You must enter a name',
          }}
        />
      </div>

      <div className="sui-mt-2">
        <Controller
          as={
            <Select
              options={programOptions}
              label="Program"
              required
              name="program"
              errors={
                reactHookForm.errors.program
                  ? [reactHookForm.errors.program.message as string]
                  : []
              }
            />
          }
          name="program"
          control={reactHookForm.control}
          defaultValue={''}
          rules={{
            required: 'You must select a program',
          }}
        />
      </div>

      <div className="sui-mt-2">
        {seasonOptions.length > 0 && (
          <Controller
            as={
              <Select
                options={seasonOptions}
                label="Season"
                required
                name="season"
                errors={
                  reactHookForm.errors.season
                    ? [reactHookForm.errors.season.message as string]
                    : []
                }
              />
            }
            name="season"
            control={reactHookForm.control}
            defaultValue={''}
            rules={{
              required: 'You must select a season',
            }}
          />
        )}

        {seasonOptions?.length === 0 && (
          <p className="sui-text-body sui-mt-2">
            There is no eligible season in this program. Please create a new
            season in this program first.
          </p>
        )}
      </div>

      <div className="sui-mt-2">
        {isLoading && (
          <>
            <Skeleton className="sui-w-[302px] sui-mb-2 sui-rounded" />
            <Skeleton className="sui-w-[220px] sui-mb-[4px] sui-rounded" />
            <Skeleton className="sui-w-full sui-h-[50px] sui-rounded" />
          </>
        )}
        {!isLoading && ngbIntegrationData && !shouldHideHCR && (
          <NgbIntegrationFields
            ngbIntegrationData={ngbIntegrationData}
            reactHookForm={reactHookForm}
            formData={formData}
          />
        )}
      </div>
    </div>
  );
};

export default DuplicateReg2FormStepOne;
